import { Controller } from '@hotwired/stimulus';

/**
 * Slim phone controller — only handles deletion.
 * Create / Edit is delegated to the generic `modal-form` controller.
 */
export default class extends Controller {

    static targets = ['number'];

    static values = {
        deleteUrl: String,
    };

    declare readonly numberTarget: HTMLInputElement;
    declare readonly hasNumberTarget: boolean;
    declare readonly deleteUrlValue: string;

    connect(): void {
        if (this.hasNumberTarget) {
            this.formatInput(this.numberTarget);
        }
    }

    formatNumber(event: Event): void {
        this.formatInput(event.currentTarget as HTMLInputElement);
    }

    async deletePhone(e: Event): Promise<void> {
        e.preventDefault();
        const btn = e.currentTarget as HTMLElement;
        const id  = btn.dataset.id;
        if (!id) return;

        const url = this.deleteUrlValue.replace('/0', '/' + id);
        const res = await fetch(url, {
            method: 'DELETE',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const json = await res.json();
        if (!json.success) return;

        const row = btn.closest('[data-phone-id]');
        if (row) row.remove();

        // If no phones left, show the empty state
        const tbody = this.element.querySelector('tbody');
        if (tbody && tbody.querySelectorAll('tr').length === 0) {
            const tableWrapper = this.element.querySelector('.table-responsive');
            if (tableWrapper) {
                tableWrapper.outerHTML =
                    `<div class="alert alert-info mb-0">${(this.element as HTMLElement).dataset.phoneEmptyLabel ?? 'No hay teléfonos registrados.'}</div>`;
            }
        }
    }

    private formatInput(input: HTMLInputElement): void {
        const masks = this.getMasks(input);
        const digits = input.value.replace(/\D/g, '');
        const mask = this.pickMask(masks, digits.length);
        if (!mask) return;

        let formatted = '';
        let index = 0;

        for (const char of mask) {
            if (index >= digits.length) break;
            if (char === '#') {
                formatted += digits[index];
                index++;
                continue;
            }
            formatted += char;
        }

        input.value = formatted;
    }

    private getMasks(input: HTMLInputElement): string[] {
        const primaryMask = input.dataset.phoneMask ?? '';
        const rawMasks = input.dataset.phoneMasks ?? '';
        let masks: string[] = [];

        if (rawMasks !== '') {
            try {
                const parsed = JSON.parse(rawMasks);
                if (Array.isArray(parsed)) {
                    masks = parsed.filter((mask): mask is string => typeof mask === 'string' && mask !== '');
                }
            } catch {
                masks = [];
            }
        }

        if (masks.length === 0 && primaryMask !== '') {
            masks = [primaryMask];
        }

        return masks;
    }

    private pickMask(masks: string[], digitsLength: number): string {
        if (masks.length === 0) return '';

        const sortedMasks = [...masks].sort((a, b) => this.countDigits(a) - this.countDigits(b));
        const exactMask = sortedMasks.find((mask) => this.countDigits(mask) === digitsLength);
        if (exactMask) return exactMask;

        return sortedMasks.find((mask) => this.countDigits(mask) >= digitsLength) ?? sortedMasks[sortedMasks.length - 1];
    }

    private countDigits(mask: string): number {
        return (mask.match(/#/g) ?? []).length;
    }
}
